/**
 * RaceStrategyPanel Component
 * 
 * Displays the non-linear, per-stint strategy optimizer output.
 * Shows a side-by-side comparison of different stop counts (e.g. "10 vs 11 vs 12 stops")
 * with concrete fuel targets (L/lap), expected lap times, and per-stint roadmap.
 * 
 * Also shows driver capability analysis (warmup-filtered), target tracking,
 * and adaptive recalculation warnings.
 */

import React, { useMemo, useState } from "react";
import { optimizeStrategy, StrategyComparison, StintTarget } from "../utils/raceStrategyOptimizer";
import type { PitStopDetails } from "../types/strategy";
import type { LiveStrategyInput, CarClass } from "../utils/liveStrategyCalculator";
import { LiveStrategyCalculator } from "../utils/liveStrategyCalculator";

interface RaceStrategyPanelProps {
  laps: Array<{ lapNumber: number; lapTime: number | null; fuelLevel: number | null }>;
  currentLap: number;
  currentFuel: number | null;
  avgLapTime: number | null;
  avgFuelPerLap: number | null;
  raceDurationMinutes: number;
  fuelCapacity: number;
  isMultiClass?: boolean;
  fastestClassLapTime?: number | null;
  carClass?: string | null;
  pitStopDetails?: PitStopDetails | null;
}

const CAR_CLASSES: { value: CarClass | 'none'; label: string }[] = [
  { value: 'GTP', label: 'GTP (LMDh)' },
  { value: 'LMP2', label: 'LMP2' },
  { value: 'GT3', label: 'GT3' },
  { value: 'none', label: 'Custom / Unknown' },
];

function formatLapTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds - mins * 60;
  return `${mins}:${secs < 10 ? '0' : ''}${secs.toFixed(3)}`;
}

function modeColor(mode: StintTarget['mode']): string {
  switch (mode) {
    case 'push':   return 'text-red-400';
    case 'save':   return 'text-yellow-400';
    case 'normal': return 'text-green-400';
  }
}

function modeLabel(mode: StintTarget['mode']): string {
  switch (mode) {
    case 'push':   return 'PUSH';
    case 'save':   return 'SAVE';
    case 'normal': return 'NORMAL';
  }
}

function modeBg(mode: StintTarget['mode']): string {
  switch (mode) {
    case 'push':   return 'bg-red-500/20 border-red-500/40';
    case 'save':   return 'bg-yellow-500/20 border-yellow-500/40';
    case 'normal': return 'bg-green-500/20 border-green-500/40';
  }
}

export const RaceStrategyPanel: React.FC<RaceStrategyPanelProps> = ({
  laps,
  currentLap,
  currentFuel,
  avgLapTime,
  avgFuelPerLap,
  raceDurationMinutes,
  fuelCapacity,
  isMultiClass,
  fastestClassLapTime,
  carClass: initialCarClass,
  pitStopDetails,
}) => {
  const [selectedClass, setSelectedClass] = useState<CarClass | 'none'>(
    (initialCarClass as CarClass) || 'GTP'
  );
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);

  const liveCalc = useMemo(() => new LiveStrategyCalculator(), []);

  const comparison: StrategyComparison | null = useMemo(() => {
    if (!avgLapTime || !avgFuelPerLap || currentFuel === null || currentFuel === undefined) {
      return null;
    }

    const effectiveClass = selectedClass === 'none' ? undefined : selectedClass;

    const input: LiveStrategyInput = {
      currentLap,
      currentFuel,
      avgLapTime,
      avgFuelPerLap,
      raceDurationMinutes,
      fuelCapacity,
      isMultiClass: isMultiClass ?? false,
      fastestClassLapTime: fastestClassLapTime ?? undefined,
      carClass: effectiveClass,
      // Pass detailed pit stop breakdown for dynamic pit time calculation
      pitInTime: pitStopDetails?.pitInTime,
      pitOutTime: pitStopDetails?.pitOutTime,
      refuelRate: pitStopDetails?.refuelTime,
    };

    // Calculate myTotalLaps using the live calculator's logic
    const liveResult = liveCalc.calculate(input);
    const myTotalLaps = liveResult.myTotalLaps;

    return optimizeStrategy(input, laps, myTotalLaps);
  }, [liveCalc, avgLapTime, avgFuelPerLap, currentFuel, currentLap, raceDurationMinutes, fuelCapacity, isMultiClass, fastestClassLapTime, selectedClass, laps]);

  if (!comparison) {
    return null;
  }

  const { recommended, plans, currentAction, currentTargetFuelPerLap, currentExpectedLapTime, driverBestLapTime, driverBestFuelPerLap, driverOnTarget, targetGap, cleanLapCount } = comparison;

  return (
    <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-semibold text-white">Race Strategy Optimizer</h2>
          <p className="text-slate-400 text-sm mt-1">
            Non-linear per-stint strategy — compares stop counts to find the fastest plan
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-400">Car Class:</label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value as CarClass | 'none')}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {CAR_CLASSES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Current Action: Big Bold Instruction ───────────────────────── */}
      <div className={`rounded-xl p-5 mb-6 border ${modeBg(currentAction)}`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Current Instruction</div>
            <div className={`text-3xl font-black ${modeColor(currentAction)}`}>
              {modeLabel(currentAction)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-400 mb-1">Fuel Target</div>
            <div className="text-3xl font-black text-white">
              {currentTargetFuelPerLap.toFixed(2)} <span className="text-lg text-slate-400">L/lap</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-400 mb-1">Expected Pace</div>
            <div className="text-2xl font-bold text-white">
              {formatLapTime(currentExpectedLapTime)}
            </div>
          </div>
        </div>
      </div>

      {/* ── Target Tracking Warning ──────────────────────────────────── */}
      {!driverOnTarget && targetGap && (
        <div className={`rounded-lg p-4 mb-5 border ${
          targetGap.shouldRecalculate
            ? 'bg-red-900/20 border-red-500/40 text-red-300'
            : 'bg-amber-900/20 border-amber-500/40 text-amber-300'
        }`}>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">{targetGap.shouldRecalculate ? '⚠' : 'ℹ'}</span>
            <span className="font-semibold text-sm">
              {targetGap.shouldRecalculate ? 'Strategy Recalculated' : 'Target Gap Detected'}
            </span>
          </div>
          <p className="text-sm">{targetGap.message}</p>
        </div>
      )}

      {/* ── Driver Stats (clean data, warmup excluded) ───────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-slate-400 text-xs">Clean Laps</div>
          <div className="text-lg font-bold text-white">{cleanLapCount}</div>
          <div className="text-xs text-slate-500">warmup excluded</div>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-slate-400 text-xs">Best Pace</div>
          <div className="text-lg font-bold text-green-400">{formatLapTime(driverBestLapTime)}</div>
          <div className="text-xs text-slate-500">proven best</div>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-slate-400 text-xs">Best Economy</div>
          <div className="text-lg font-bold text-blue-400">{driverBestFuelPerLap.toFixed(2)} L</div>
          <div className="text-xs text-slate-500">lowest achieved</div>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-slate-400 text-xs">Recommended</div>
          <div className="text-lg font-bold text-white">{recommended.stops} stop{recommended.stops !== 1 ? 's' : ''}</div>
          <div className="text-xs text-slate-500">fastest plan</div>
        </div>
      </div>

      {/* ── Stop Count Comparison Table ───────────────────────────────── */}
      <h3 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wide">
        Stop Count Comparison
      </h3>
      <div className="space-y-2 mb-6">
        {plans.map((plan) => {
          const isRec = plan === recommended;
          const isExpanded = expandedPlan === plan.stops;

          return (
            <div key={plan.stops}>
              {/* Plan Summary Row */}
              <button
                onClick={() => setExpandedPlan(isExpanded ? null : plan.stops)}
                className={`w-full text-left rounded-lg p-4 border transition-colors ${
                  isRec
                    ? 'bg-green-900/20 border-green-500/40 hover:bg-green-900/30'
                    : plan.feasible
                      ? 'bg-slate-900/50 border-slate-700 hover:bg-slate-700/50'
                      : 'bg-slate-900/30 border-slate-700/50 opacity-60'
                }`}
              >
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    {isRec && <span className="text-green-400 text-lg">★</span>}
                    <div>
                      <span className={`font-bold ${isRec ? 'text-green-400' : plan.feasible ? 'text-white' : 'text-slate-500'}`}>
                        {plan.stops} stop{plan.stops !== 1 ? 's' : ''}
                      </span>
                      {!plan.feasible && (
                        <span className="ml-2 text-xs text-red-400">(infeasible)</span>
                      )}
                    </div>
                  </div>

                  {plan.feasible && (
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <div className="text-slate-400 text-xs">Fuel Target</div>
                        <div className="font-mono font-bold text-white">
                          {plan.avgTargetFuelPerLap.toFixed(2)} L/lap
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-slate-400 text-xs">Avg Pace</div>
                        <div className="font-mono font-bold text-white">
                          {formatLapTime(plan.avgExpectedLapTime)}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-slate-400 text-xs">Fuel Save</div>
                        <div className="font-mono font-bold text-yellow-400">
                          {plan.avgFuelSavePercent > 0.5 ? `${plan.avgFuelSavePercent.toFixed(1)}%` : '—'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-slate-400 text-xs">Total Time</div>
                        <div className={`font-mono font-bold ${isRec ? 'text-green-400' : 'text-white'}`}>
                          {formatLapTime(plan.totalRaceTime)}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-slate-400 text-xs">Delta</div>
                        <div className={`font-mono font-bold ${
                          plan.timeDeltaVsBest === 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {plan.timeDeltaVsBest === 0
                            ? 'BEST'
                            : `+${plan.timeDeltaVsBest.toFixed(1)}s`}
                        </div>
                      </div>
                    </div>
                  )}

                  {!plan.feasible && (
                    <div className="text-xs text-red-400">{plan.infeasibleReason}</div>
                  )}

                  <span className="text-slate-500 text-xs">
                    {isExpanded ? '▲' : '▼'}
                  </span>
                </div>
              </button>

              {/* Expanded: Per-Stint Breakdown */}
              {isExpanded && plan.feasible && (
                <div className="mt-1 ml-4 mr-4 rounded-b-lg bg-slate-900/60 p-4 border-x border-b border-slate-700">
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                    Per-Stint Roadmap
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-700 text-slate-400 text-xs">
                          <th className="px-2 py-1.5 text-left">Stint</th>
                          <th className="px-2 py-1.5 text-center">Mode</th>
                          <th className="px-2 py-1.5 text-right">Laps</th>
                          <th className="px-2 py-1.5 text-right">Fuel Target</th>
                          <th className="px-2 py-1.5 text-right">Expected Pace</th>
                          <th className="px-2 py-1.5 text-right">Save %</th>
                          <th className="px-2 py-1.5 text-right">Fuel Start</th>
                          <th className="px-2 py-1.5 text-right">Fuel End</th>
                          <th className="px-2 py-1.5 text-center">Pit After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plan.stints.map((stint) => (
                          <tr key={stint.stintNumber} className="border-b border-slate-700/50">
                            <td className="px-2 py-1.5 font-mono">
                              {stint.stintNumber === 1 ? (
                                <span className="text-blue-400 font-bold">→ {stint.stintNumber}</span>
                              ) : stint.stintNumber}
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${modeBg(stint.mode)} ${modeColor(stint.mode)}`}>
                                {modeLabel(stint.mode)}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono">{stint.laps}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-bold text-white">
                              {stint.targetFuelPerLap.toFixed(2)} L
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-white">
                              {formatLapTime(stint.expectedLapTime)}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono">
                              {stint.fuelSavePercent > 0.5 ? (
                                <span className="text-yellow-400">{stint.fuelSavePercent.toFixed(1)}%</span>
                              ) : (
                                <span className="text-slate-500">—</span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-slate-300">
                              {stint.fuelAtStart.toFixed(1)}L
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-slate-300">
                              {stint.fuelAtEnd.toFixed(1)}L
                            </td>
                            <td className="px-2 py-1.5 text-center text-xs">
                              {stint.pitAfter ? (
                                <span className={stint.pitAfter === 'full' ? 'text-green-400' : 'text-orange-400'}>
                                  {stint.pitAfter === 'full' ? 'Full' : 'Splash'}
                                </span>
                              ) : (
                                <span className="text-slate-500">Finish</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Data Quality Note ────────────────────────────────────────── */}
      <div className="text-xs text-slate-500 flex items-center gap-2">
        <span>
          Based on {cleanLapCount} clean lap{cleanLapCount !== 1 ? 's' : ''} (warmup + outliers excluded).
          {cleanLapCount < 5 && ' Strategy will improve with more laps.'}
        </span>
      </div>
    </div>
  );
};
